import {
  type GameType,
  getMachinesSearchTaggingOptions,
  type MachinePublicHydrated,
  type SportType,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { TeamSide } from "../constants/teams";
import { GameTeamsBySide, toTeamDisplays } from "../utils/teamColors";

/**
 * The machines search sends the field's configured sport, but the omnibus
 * spec doesn't list it yet. Not every field has one.
 */
type MachineWithSport = MachinePublicHydrated & { sport?: string | null };

/** A sport omnibus accepts, or null for anything else. */
export const toSportType = (
  sport: string | null | undefined,
): SportType | null =>
  sport === "baseball" || sport === "softball" ? sport : null;

/** Sessions without a type predate bullpen/BP, so they're games. */
export const isGameSession = (type: GameType | null | undefined) =>
  type !== "bullpen" && type !== "batting_practice";

/**
 * The SmartPark's current omnibus game (id + team ids), from the same
 * machines search the /tagging-v2/:id loader uses.
 */
export const useActiveGame = (
  /** The game service's teams: keeps each team's color through a swap */
  gameTeams?: GameTeamsBySide,
) => {
  const { id: machineUuid } = useParams() as { id: string };

  const { data: machine, isLoading } = useQuery({
    ...getMachinesSearchTaggingOptions(),
    staleTime: 60_000,
    select: (res): MachineWithSport | undefined =>
      res.machines.find((m) => m.machineUuid === machineUuid),
  });

  // A bullpen or batting practice session isn't a game
  const session = machine?.currentGame;
  const game = isGameSession(session?.type) ? session : undefined;
  const home = game?.teams?.home;
  const away = game?.teams?.away;
  const teamIds = useMemo<Record<TeamSide, string> | undefined>(
    () => (home && away ? { home, away } : undefined),
    [home, away],
  );

  const savedColors = game?.teamColors;
  const teamDisplays = useMemo(
    () => toTeamDisplays(savedColors, teamIds, gameTeams),
    [savedColors, teamIds, gameTeams],
  );

  return {
    machineUuid,
    gameId: game?.gameUuid ?? undefined,
    teamIds,
    /** The omnibus game's parts, which an end time edit sends back */
    times: game?.times,
    /** Each team's color, from the colors saved when the game was scheduled */
    teamDisplays,
    /** The field's configured sport, if it has one */
    fieldSport: toSportType(machine?.sport),
    isLoading,
  };
};
